import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import useCompanyId from '../components/e2i/useCompanyId';
import InquiryTable, { InquiryColumn } from '../components/inquiry/InquiryTable';
import SapPODialog from '../components/inquiry/SapPODialog';
import {
  DelayedTextField,
  DocLinks,
  MultiPicker,
  codeOptions,
  codesText,
  hasModule,
  joinList,
  money,
  splitList,
  statusColor,
  useUrlFilters,
} from '../components/inquiry/inquiryCommon';
import inquiryApi from '../services/inquiryApi';
import { showSnackbar } from '../store/slices/uiSlice';
import type { RootState } from '../store';
import type { PoInquiryResult, PoInquiryRow, PoLookups } from '../types/inquiry';
import { saveBlob } from '../utils/formattedSoa';
import { blobErrorText, cleanDateInput, errorText, formatDate, formatDateTime, todayIso } from '../utils/e2i';

/**
 * PO Inquiry: search the purchase orders of the selected company the user can read, PRPO's own and those created in
 * SAP (from PRPO's synced copy of SAP's POs), with their PRs and, for SAP POs, a read-only view with the goods
 * receipts. A PRPO PO opens on its page; one posted to SAP also opens in the SAP view. The filters stay in the
 * address, so Back returns to the search.
 */
const KEYS = ['q', 'source', 'poNumber', 'vendor', 'status', 'project', 'division', 'department', 'requester', 'dateFrom',
  'dateTo', 'dueFrom', 'dueTo', 'amountMin', 'amountMax', 'item', 'prNumber', 'receiving', 'sort', 'dir', 'page', 'pageSize'] as const;
const MORE = ['project', 'division', 'department', 'requester', 'dateFrom', 'dateTo', 'dueFrom', 'dueTo', 'amountMin', 'amountMax',
  'item', 'prNumber', 'receiving'];

const POInquiry: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((s: RootState) => s.auth.user);
  const { companyId, companyName } = useCompanyId();
  const { filters, update, clear } = useUrlFilters(KEYS, 'prpo.poInquiry.search');
  const [lookups, setLookups] = useState<PoLookups | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [result, setResult] = useState<PoInquiryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [showMore, setShowMore] = useState(() => MORE.some((k) => !!new URLSearchParams(window.location.search).get(k)));
  const [sapPo, setSapPo] = useState<number | null>(null);
  const ticket = useRef(0);
  const canPrInquiry = hasModule(user, 'pr-inquiry');

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    setLookupError(null);
    inquiryApi.poLookups(companyId)
      .then((l) => { if (!cancelled) setLookups(l); })
      .catch((e: any) => { if (!cancelled) setLookupError(errorText(e, 'The PO Inquiry could not be opened for this company')); });
    return () => { cancelled = true; };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    const mine = ++ticket.current;
    setLoading(true);
    setError(null);
    inquiryApi.poSearch(companyId, filters)
      .then((r) => { if (mine === ticket.current) setResult(r); })
      .catch((e: any) => { if (mine === ticket.current) { setResult(null); setError(errorText(e, 'The search failed')); } })
      .finally(() => { if (mine === ticket.current) setLoading(false); });
  }, [companyId, filters]);

  const set = useCallback((key: string) => (value: string) => update({ [key]: value }), [update]);
  const setList = (key: string) => (values: Array<string | number>) => update({ [key]: joinList(values) });
  const setDate = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => update({ [key]: cleanDateInput(e.target.value) });

  const companyCurrency = result?.currency || lookups?.company.currency || '';
  const decimals = result?.amountDecimals ?? lookups?.company.amountDecimals ?? 2;
  const amount = (n: number, currency: string) => money(n, currency || companyCurrency, companyCurrency, decimals);
  const names = result?.names || { projects: {}, divisions: {}, departments: {} };
  const sync = result?.sapSync || lookups?.sapSync;
  const active = KEYS.filter((k) => !['sort', 'dir', 'page', 'pageSize'].includes(k)).some((k) => !!filters[k]);

  const open = (r: PoInquiryRow) => {
    if (r.source === 'PRPO') navigate(`/po/${r.id}`);
    else setSapPo(r.id);
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const blob = await inquiryApi.poExport(companyId, filters);
      saveBlob(blob, `PO-Inquiry-${todayIso()}.xlsx`);
    } catch (e: any) {
      dispatch(showSnackbar({ message: await blobErrorText(e, 'The Excel export failed'), severity: 'error' }));
    } finally {
      setExporting(false);
    }
  };

  const columns: InquiryColumn<PoInquiryRow>[] = useMemo(() => [
    {
      id: 'number', header: 'PO No.', sortKey: 'number', minWidth: 150,
      cell: (r) => (
        <Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" fontWeight={600} color="primary.main">
              {r.source === 'PRPO' ? r.poNumber : `SAP ${r.sapDocNum ?? r.id}`}
            </Typography>
            {r.source === 'PRPO' && r.sapDocEntry && (
              <Tooltip title="SAP view with goods receipts">
                <IconButton
                  size="small"
                  aria-label={`Open SAP purchase order ${r.sapDocNum ?? ''} with its goods receipts`}
                  onClick={(e) => { e.stopPropagation(); setSapPo(r.sapDocEntry); }}
                >
                  <InventoryIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {r.source === 'PRPO' ? `PRPO${r.sapDocNum ? ` · SAP ${r.sapDocNum}` : ''}` : 'Created in SAP'}
          </Typography>
        </Box>
      ),
    },
    { id: 'date', header: 'PO date', sortKey: 'date', cell: (r) => formatDate(r.date) },
    {
      id: 'vendor', header: 'Vendor', sortKey: 'vendor', minWidth: 200,
      cell: (r) => (
        <Box>
          <Typography variant="body2">{r.vendorName}</Typography>
          <Typography variant="caption" color="text.secondary">{r.vendorCode}</Typography>
        </Box>
      ),
    },
    {
      id: 'status', header: 'Status', sortKey: 'status', minWidth: 150,
      cell: (r) => (
        <Stack spacing={0.5} alignItems="flex-start">
          <Chip size="small" color={statusColor(r.sapStatus && !r.prpoStatus ? r.sapStatus : r.prpoStatus)} label={r.statusLabel} />
          {r.receiving && <Chip size="small" variant="outlined" color={r.receiving === 'Not received' ? 'warning' : 'info'} label={r.receiving} />}
        </Stack>
      ),
    },
    { id: 'amount', header: 'Total', sortKey: 'amount', align: 'right', minWidth: 130, cell: (r) => <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{amount(r.amount, r.currency)}</Box> },
    { id: 'projects', header: 'Projects', minWidth: 160, cell: (r) => codesText(r.projects, 'projects', names) },
    { id: 'divisions', header: 'Divisions', minWidth: 120, cell: (r) => codesText(r.divisions, 'divisions', names) },
    { id: 'departments', header: 'Departments', minWidth: 120, cell: (r) => codesText(r.departments, 'departments', names) },
    {
      id: 'prs', header: 'Purchase requisitions', minWidth: 160,
      cell: (r) => (
        <Box>
          <DocLinks
            items={r.prs}
            label={(p) => p.number}
            ariaLabel={(p) => `Open purchase requisition ${p.number}`}
            onOpen={(p) => { if (p.id) navigate(`/pr/${p.id}`); }}
          />
          {canPrInquiry && r.prs.length > 0 && (
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                navigate(`/pr-inquiry?poNumber=${encodeURIComponent(r.source === 'PRPO' ? r.poNumber || '' : String(r.sapDocNum ?? ''))}`);
              }}
            >
              In PR Inquiry
            </Link>
          )}
        </Box>
      ),
    },
    { id: 'due', header: 'Delivery date', sortKey: 'due', cell: (r) => formatDate(r.dueDate) },
    { id: 'requester', header: 'Requested by', minWidth: 130, cell: (r) => r.requester },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [names, companyCurrency, decimals, canPrInquiry, navigate]);

  if (!companyId) return <Alert severity="info" sx={{ m: 2 }}>Select a company first.</Alert>;

  const companyLabel = lookups?.company.name || companyName || 'this company';
  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <Box>
        <Typography variant="h5" component="h1">PO Inquiry</Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.role === 'Admin'
            ? `Every purchase order of ${companyLabel}, PRPO's and those created in SAP.`
            : `Purchase orders of ${companyLabel}, PRPO's and those created in SAP: yours, those you approve, and those of the projects, divisions and departments granted to you.`}
        </Typography>
        {sync && (sync.count > 0 ? (
          <Typography variant="caption" color="text.secondary">
            SAP purchase orders as PRPO last synced them{sync.syncedAt ? ` (${formatDateTime(sync.syncedAt)})` : ''}; an opened SAP PO is read live.
          </Typography>
        ) : (
          <Alert severity="info" sx={{ mt: 1 }}>
            PRPO does not sync this company's SAP purchase orders yet, so only PRPO's own POs are listed. An administrator can
            add a Purchase Orders job in Settings › Schedule Jobs.
          </Alert>
        ))}
      </Box>
      {lookupError && <Alert severity="error">{lookupError}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-label="Search filters">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <DelayedTextField id="poi-q" label="Search" fullWidth size="small" value={filters.q || ''} onValue={set('q')}
              placeholder="PO number, vendor, item, PR number, remarks…" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <DelayedTextField id="poi-number" label="PO No." fullWidth size="small" value={filters.poNumber || ''} onValue={set('poNumber')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <DelayedTextField id="poi-vendor" label="Vendor" fullWidth size="small" value={filters.vendor || ''} onValue={set('vendor')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={8} md={2.5}>
            <MultiPicker id="poi-status" label="Status" value={splitList(filters.status)} onChange={setList('status')}
              options={(lookups?.statuses || []).map((s) => ({ value: s.value, label: s.label }))} />
          </Grid>
          <Grid item xs={12} sm={4} md={1.5}>
            <TextField id="poi-source" select label="Created in" fullWidth size="small" value={filters.source || ''} onChange={(e) => update({ source: e.target.value })}
              InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">PRPO and SAP</MenuItem>
              <MenuItem value="PRPO">PRPO</MenuItem>
              <MenuItem value="SAP">SAP</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        <Collapse in={showMore} unmountOnExit>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6} md={4}>
              <MultiPicker id="poi-project" label="Projects" value={splitList(filters.project)} onChange={setList('project')} options={codeOptions(lookups?.codes.projects || [])} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MultiPicker id="poi-division" label="Divisions" value={splitList(filters.division)} onChange={setList('division')} options={codeOptions(lookups?.codes.divisions || [])} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MultiPicker id="poi-department" label="Departments" value={splitList(filters.department)} onChange={setList('department')} options={codeOptions(lookups?.codes.departments || [])} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField id="poi-date-from" type="date" label="PO date from" fullWidth size="small" value={filters.dateFrom || ''} onChange={setDate('dateFrom')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField id="poi-date-to" type="date" label="PO date to" fullWidth size="small" value={filters.dateTo || ''} onChange={setDate('dateTo')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField id="poi-due-from" type="date" label="Delivery from" fullWidth size="small" value={filters.dueFrom || ''} onChange={setDate('dueFrom')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField id="poi-due-to" type="date" label="Delivery to" fullWidth size="small" value={filters.dueTo || ''} onChange={setDate('dueTo')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <DelayedTextField id="poi-amount-min" label="Total from" fullWidth size="small" value={filters.amountMin || ''} onValue={set('amountMin')} inputProps={{ inputMode: 'decimal' }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <DelayedTextField id="poi-amount-max" label="Total to" fullWidth size="small" value={filters.amountMax || ''} onValue={set('amountMax')} inputProps={{ inputMode: 'decimal' }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DelayedTextField id="poi-item" label="Item (code or description)" fullWidth size="small" value={filters.item || ''} onValue={set('item')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <DelayedTextField id="poi-pr" label="PR No." fullWidth size="small" value={filters.prNumber || ''} onValue={set('prNumber')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField id="poi-receiving" select label="Receiving" fullWidth size="small" value={filters.receiving || ''} onChange={(e) => update({ receiving: e.target.value })}
                InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="notReceived">Open, not received</MenuItem>
                <MenuItem value="partlyReceived">Open, partly received</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MultiPicker id="poi-requester" label="Requested by (PRPO POs)" value={splitList(filters.requester)} onChange={setList('requester')}
                options={(lookups?.requesters || []).map((r) => ({ value: String(r.id), label: r.name || `User ${r.id}` }))} />
            </Grid>
          </Grid>
        </Collapse>
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Button size="small" startIcon={<FilterListIcon />} onClick={() => setShowMore(!showMore)} aria-expanded={showMore}>
            {showMore ? 'Fewer filters' : 'More filters'}
          </Button>
          <Button size="small" startIcon={<ClearIcon />} onClick={clear} disabled={!active}>Clear filters</Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportExcel} disabled={exporting || !result?.total}>
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="body2" color="text.secondary" aria-live="polite">
        {result
          ? `${result.total.toLocaleString()} purchase order${result.total === 1 ? '' : 's'}${result.totals.length ? ` · ${result.totals.map((t) => amount(t.amount, t.currency)).join(' · ')}` : ''}`
          : loading ? 'Searching…' : ''}
      </Typography>

      <InquiryTable
        ariaLabel="Purchase orders"
        columns={columns}
        rows={result?.rows || []}
        getRowKey={(r) => `${r.source}:${r.id}`}
        rowLabel={(r) => `Open purchase order ${r.poNumber || r.sapDocNum || r.id}`}
        onRowClick={open}
        sort={filters.sort || 'date'}
        dir={filters.dir === 'asc' ? 'asc' : 'desc'}
        onSort={(sort, dir) => update({ sort, dir })}
        page={Number(filters.page) || 1}
        pageSize={Number(filters.pageSize) || 50}
        total={result?.total || 0}
        onPage={(page) => update({ page: String(page) })}
        onPageSize={(size) => update({ pageSize: String(size) })}
        loading={loading}
        emptyMessage={active ? 'No purchase order matches these filters.' : 'No purchase order to show.'}
      />

      <SapPODialog
        open={sapPo !== null}
        companyId={companyId}
        companyCurrency={companyCurrency}
        docEntry={sapPo}
        onClose={() => setSapPo(null)}
        onOpenPr={(pr) => { setSapPo(null); if (pr.id) navigate(`/pr/${pr.id}`); }}
        onOpenPrpoPo={(id) => { setSapPo(null); navigate(`/po/${id}`); }}
      />
    </Box>
  );
};

export default POInquiry;
